import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AddressSchema, { AddressJSON, AddressParams } from "_shared/addresses/address-schema";
import Coordinates from "_shared/addresses/coordinates/coordinates";
import { isEmpty } from "lodash";

export default class Address {
  public static get EXAMPLE(): Address {
    return Address.create({
      address1: "123 Test St",
      address2: "Suite 100",
      city: "Testville",
      state: "TS",
      postalCode: "12345",
      country: "Testland",
      coordinates: Coordinates.create({
        latitude: "12.345678",
        longitude: "98.7654321",
      }),
    });
  }

  public static create(values: AddressParams): Address {
    const parsed = AddressSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new InvalidAddressError({
        values,
        message: "Invalid Address parameters.",
      });
    }
    return new Address(parsed);
  }

  public static fromJSON(json: AddressJSON | null | undefined): Address | null | undefined {
    if (isNullOrUndefined(json)) return json;
    const params = AddressSchema.deserialize(json);
    return new Address(params);
  }

  public readonly address1: string;
  public readonly address2?: string;
  public readonly city?: string;
  public readonly state?: string;
  public readonly postalCode?: string;
  public readonly country?: string;
  public readonly coordinates?: Coordinates;

  public get complete(): string {
    return this.toString();
  }

  public constructor(values: AddressParams) {
    this.address1 = values.address1;
    this.address2 = values.address2;
    this.city = values.city;
    this.state = values.state;
    this.postalCode = values.postalCode;
    this.country = values.country;
    this.coordinates = values.coordinates ? Coordinates.create(values.coordinates) : undefined;
  }

  public isEqualTo(other: Address | null | undefined): boolean {
    if (!other) return false;
    return Boolean(
      other instanceof Address &&
        this.address1 === other.address1 &&
        this.address2 === other.address2 &&
        this.city === other.city &&
        this.state === other.state &&
        this.postalCode === other.postalCode &&
        this.country === other.country &&
        ((!this.coordinates && !other.coordinates) || this.coordinates?.isEqualTo(other.coordinates))
    );
  }

  public toString(): string {
    let parts: string[] = [this.address1];
    if (this.address2) parts.push(this.address2);
    if (this.city) parts.push(this.city);
    if (this.state) parts.push(this.state);
    if (this.postalCode) parts.push(this.postalCode);
    if (this.country) parts.push(this.country);
    return parts.join(", ");
  }

  public toJSON(): AddressJSON {
    return AddressSchema.serialize(this);
  }
}

export class InvalidAddressError extends Error {
  constructor(context: { values: AddressParams; message: string }) {
    let contextString = " ";
    if (typeof context.values === "object" && !isEmpty(context.values))
      contextString = `\n${JSON.stringify(context.values)}\n-->`;

    super(`Invalid address:${contextString}${context.message}`);
  }
}
